/**
 * OnboardingTour - Web Version
 *
 * Contextual spotlight cards that guide first-time Fleet Hub users
 * through the 3 core features: booking, receipts, and live tracking.
 * Uses a spotlight cutout overlay so the target element visually pops
 * through — even if it sits behind sheets or other layers.
 *
 * @module components/OnboardingTour
 */

import { FC, useEffect, useState, useCallback } from "react"
import { View, ViewStyle, TextStyle, Pressable, useWindowDimensions } from "react-native"

import { Text } from "@/components/Text"
import { useFleetHubStore } from "@/stores/useFleetHubStore"
import { colors, spacing, breakpoints } from "@/theme"

// ── Step configuration ──────────────────────────────

interface TutorialStep {
  targetId: string
  title: string
  message: string
  position: "top" | "bottom" | "center"
}

const CARD_WIDTH = 320
const EDGE_PADDING = 16
const GAP = 12
const SPOTLIGHT_PAD = 6 // breathing room around the target

function getSteps(isMobile: boolean): TutorialStep[] {
  return [
    {
      targetId: "tutorial-booking-btn",
      title: "Inspektion buchen",
      message: "Hier buchst du neue Inspektionen.",
      position: "top",
    },
    {
      targetId: isMobile ? "tutorial-hamburger" : "tutorial-billing-btn",
      title: "Rechnungen & Berichte",
      message: isMobile
        ? "Im Menü oben rechts findest du alle Rechnungen und Prüfberichte."
        : "Hier findest du alle Rechnungen und Prüfberichte.",
      position: "bottom",
    },
    {
      targetId: "tutorial-drag-handle",
      title: "Auftragsübersicht",
      message: "Hier vergrößerst du deine Auftragsübersicht.",
      position: "top",
    },
  ]
}

// ── Positioning helpers ─────────────────────────────

interface SpotlightRect {
  top: number
  left: number
  width: number
  height: number
}

interface CardPosition {
  top?: number
  left?: number
  bottom?: number
  arrowLeft?: number
  arrowDirection: "up" | "down" | "none"
}

function clampLeft(idealLeft: number, vw: number): number {
  return Math.max(EDGE_PADDING, Math.min(idealLeft, vw - CARD_WIDTH - EDGE_PADDING))
}

function arrowOffset(targetCenterX: number, cardLeft: number): number {
  const relative = targetCenterX - cardLeft
  return Math.max(20, Math.min(relative, CARD_WIDTH - 20))
}

// ── Component ───────────────────────────────────────

export const OnboardingTour: FC = () => {
  const { width: screenWidth } = useWindowDimensions()
  const isMobile = screenWidth < breakpoints.mobile

  const showTutorial = useFleetHubStore((s) => s.showTutorial)
  const currentStep = useFleetHubStore((s) => s.currentTutorialStep)
  const nextStep = useFleetHubStore((s) => s.nextTutorialStep)
  const dismiss = useFleetHubStore((s) => s.dismissTutorial)

  const [cardPos, setCardPos] = useState<CardPosition | null>(null)
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null)
  const [visible, setVisible] = useState(false)

  const steps = getSteps(isMobile)
  const step = steps[currentStep]

  /** Measure target element and compute card + spotlight positions */
  const updateLayout = useCallback(() => {
    if (!showTutorial || !step) return

    const target = document.getElementById(step.targetId)

    // ── Spotlight rect (works on both mobile & desktop) ──
    if (target) {
      const rect = target.getBoundingClientRect()
      setSpotlight({
        top: rect.top - SPOTLIGHT_PAD,
        left: rect.left - SPOTLIGHT_PAD,
        width: rect.width + SPOTLIGHT_PAD * 2,
        height: rect.height + SPOTLIGHT_PAD * 2,
      })
    } else {
      setSpotlight(null)
    }

    // ── Card position ──
    if (isMobile) {
      setCardPos({ arrowDirection: "none" })
      setVisible(true)
      return
    }

    if (!target) {
      setCardPos(null)
      setVisible(true)
      return
    }

    const rect = target.getBoundingClientRect()
    const vw = window.innerWidth
    const vh = window.innerHeight
    const targetCenterX = rect.left + rect.width / 2

    let pos: CardPosition

    if (step.position === "top") {
      const cardLeft = clampLeft(targetCenterX - CARD_WIDTH / 2, vw)
      pos = {
        bottom: Math.max(GAP, vh - rect.top + GAP + SPOTLIGHT_PAD),
        left: cardLeft,
        arrowLeft: arrowOffset(targetCenterX, cardLeft),
        arrowDirection: "down",
      }
    } else if (step.position === "bottom") {
      const cardLeft = clampLeft(targetCenterX - CARD_WIDTH / 2, vw)
      const cardTop = rect.bottom + GAP + SPOTLIGHT_PAD
      pos = {
        top: Math.min(cardTop, vh - 220),
        left: cardLeft,
        arrowLeft: arrowOffset(targetCenterX, cardLeft),
        arrowDirection: "up",
      }
    } else {
      const cardLeft = clampLeft(rect.left + rect.width / 2 - CARD_WIDTH / 2, vw)
      pos = {
        top: Math.max(80, Math.min(rect.top + rect.height * 0.3, vh - 260)),
        left: cardLeft,
        arrowDirection: "none",
      }
    }

    setCardPos(pos)
    setVisible(true)
  }, [showTutorial, step, isMobile])

  // Recalculate on step change, resize, or scroll
  useEffect(() => {
    if (!showTutorial) {
      setVisible(false)
      return
    }

    const rafId = requestAnimationFrame(updateLayout)
    window.addEventListener("resize", updateLayout)
    window.addEventListener("scroll", updateLayout, true)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener("resize", updateLayout)
      window.removeEventListener("scroll", updateLayout, true)
    }
  }, [showTutorial, currentStep, updateLayout])

  // Inject CSS keyframes once
  useEffect(() => {
    if (typeof document === "undefined") return
    if (document.getElementById("onboarding-tour-styles")) return

    const style = document.createElement("style")
    style.id = "onboarding-tour-styles"
    style.textContent = `
      @keyframes tourCardIn {
        from { opacity: 0; transform: translateY(8px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes tourCardInMobile {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes spotlightIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
    `
    document.head.appendChild(style)
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    if (!showTutorial) return

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") dismiss()
      else if (e.key === "ArrowRight" || e.key === "Enter") nextStep()
    }

    document.addEventListener("keydown", handleKey)
    return () => document.removeEventListener("keydown", handleKey)
  }, [showTutorial, dismiss, nextStep])

  if (!showTutorial || !step || !visible) return null

  const isLastStep = currentStep === steps.length - 1

  // ── Shared card content ──
  const cardContent = (
    <>
      <View style={$cardHeader}>
        <Text style={$stepLabel}>
          {currentStep + 1}/{steps.length}
        </Text>
        <Pressable
          onPress={dismiss}
          accessibilityRole="button"
          accessibilityLabel="Tutorial überspringen"
          style={$skipPressable}
        >
          <Text style={$skipText}>Überspringen</Text>
        </Pressable>
      </View>

      <Text style={$title}>{step.title}</Text>
      <Text style={$message}>{step.message}</Text>

      <View style={$dotsRow}>
        {steps.map((_, i) => (
          <View key={i} style={i === currentStep ? $dotActive : $dot} />
        ))}
      </View>

      <Pressable onPress={nextStep} style={$nextButton} accessibilityRole="button">
        <Text style={$nextButtonText}>{isLastStep ? "Fertig" : "Weiter"}</Text>
      </Pressable>
    </>
  )

  // ── Spotlight overlay: transparent cutout + massive shadow ──
  const spotlightElement = spotlight ? (
    <View
      style={[
        $spotlightCutout,
        {
          top: spotlight.top,
          left: spotlight.left,
          width: spotlight.width,
          height: spotlight.height,
        } as any,
      ]}
      pointerEvents="none"
    />
  ) : (
    // No target found — just a full dark backdrop
    <Pressable
      onPress={dismiss}
      style={$fullBackdrop}
      accessibilityRole="button"
      accessibilityLabel="Tutorial schließen"
    />
  )

  // Clickable layer behind the card to catch "dismiss" taps.
  // The spotlight cutout is pointer-events: none so clicks pass through to the
  // real element underneath. This separate backdrop catches everything else.
  const dismissLayer = (
    <Pressable
      onPress={dismiss}
      style={$dismissLayer}
      accessibilityRole="button"
      accessibilityLabel="Tutorial schließen"
    />
  )

  // ── Mobile: bottom banner ──
  if (isMobile) {
    return (
      <>
        {dismissLayer}
        {spotlightElement}
        <View style={$mobileCard}>{cardContent}</View>
      </>
    )
  }

  // ── Desktop: positioned card ──
  const cardStyle: ViewStyle = {
    // @ts-ignore
    position: "fixed",
    ...(cardPos?.top != null ? { top: cardPos.top } : {}),
    ...(cardPos?.bottom != null ? { bottom: cardPos.bottom } : {}),
    ...(cardPos?.left != null ? { left: cardPos.left } : {}),
    width: CARD_WIDTH,
    zIndex: 9999,
    // @ts-ignore
    animation: "tourCardIn 200ms ease-out",
  }

  return (
    <>
      {dismissLayer}
      {spotlightElement}

      <View style={cardStyle as any} pointerEvents="box-none">
        {cardPos?.arrowDirection === "up" && (
          <View
            style={[
              $arrow,
              $arrowUp,
              cardPos.arrowLeft != null ? { left: cardPos.arrowLeft - 8 } : {},
            ]}
          />
        )}

        <View style={$desktopCard}>{cardContent}</View>

        {cardPos?.arrowDirection === "down" && (
          <View
            style={[
              $arrow,
              $arrowDown,
              cardPos.arrowLeft != null ? { left: cardPos.arrowLeft - 8 } : {},
            ]}
          />
        )}
      </View>
    </>
  )
}

// ── Styles ──────────────────────────────────────────

/**
 * Spotlight cutout: a transparent box positioned exactly over the target.
 * The enormous box-shadow darkens everything EXCEPT the target area.
 * border acts as a subtle glow ring around the opening.
 */
const $spotlightCutout: ViewStyle = {
  // @ts-ignore
  position: "fixed",
  zIndex: 9998,
  borderRadius: 12,
  borderWidth: 2,
  borderColor: "rgba(255, 56, 47, 0.5)",
  // @ts-ignore — huge spread creates the overlay
  boxShadow: "0 0 0 9999px rgba(17, 30, 44, 0.45)",
  // @ts-ignore
  animation: "spotlightIn 250ms ease-out",
}

/** Clickable dismiss layer behind everything (transparent) */
const $dismissLayer: ViewStyle = {
  // @ts-ignore
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9997,
}

/** Fallback full backdrop when no target element exists */
const $fullBackdrop: ViewStyle = {
  // @ts-ignore
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(17, 30, 44, 0.45)",
  zIndex: 9998,
}

const $desktopCard: ViewStyle = {
  backgroundColor: "rgba(255, 255, 255, 0.95)",
  borderRadius: 14,
  padding: spacing.lg,
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  boxShadow: "0 4px 24px rgba(17, 30, 44, 0.18), 0 1px 0 rgba(17, 30, 44, 0.06)",
}

const $mobileCard: ViewStyle = {
  // @ts-ignore
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  zIndex: 9999,
  backgroundColor: "rgba(255, 255, 255, 0.97)",
  borderTopLeftRadius: 20,
  borderTopRightRadius: 20,
  padding: spacing.lg,
  paddingBottom: 32,
  // @ts-ignore
  backdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  WebkitBackdropFilter: "blur(20px) saturate(1.2)",
  // @ts-ignore
  boxShadow: "0 -4px 24px rgba(17, 30, 44, 0.15)",
  // @ts-ignore
  animation: "tourCardInMobile 250ms ease-out",
}

const $cardHeader: ViewStyle = {
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: spacing.sm,
}

const $stepLabel: TextStyle = {
  fontSize: 12,
  fontWeight: "600",
  color: colors.palette.primary,
  letterSpacing: 0.5,
}

const $skipPressable: ViewStyle = {
  paddingVertical: 4,
  paddingHorizontal: 8,
  // @ts-ignore
  cursor: "pointer",
}

const $skipText: TextStyle = {
  fontSize: 13,
  fontWeight: "500",
  color: colors.textDim,
}

const $title: TextStyle = {
  fontSize: 17,
  fontWeight: "700",
  color: colors.text,
  marginBottom: 4,
}

const $message: TextStyle = {
  fontSize: 14,
  fontWeight: "400",
  color: colors.textDim,
  lineHeight: 21,
  marginBottom: spacing.md,
}

const $dotsRow: ViewStyle = {
  flexDirection: "row",
  gap: 6,
  marginBottom: spacing.md,
}

const $dot: ViewStyle = {
  width: 6,
  height: 6,
  borderRadius: 3,
  backgroundColor: colors.palette.neutral300,
}

const $dotActive: ViewStyle = {
  width: 6,
  height: 6,
  borderRadius: 3,
  backgroundColor: colors.palette.primary,
}

const $nextButton: ViewStyle = {
  backgroundColor: colors.palette.neutral800,
  borderRadius: 10,
  paddingVertical: 10,
  paddingHorizontal: spacing.lg,
  alignItems: "center",
  // @ts-ignore
  cursor: "pointer",
  // @ts-ignore
  transition: "all 150ms ease",
}

const $nextButtonText: TextStyle = {
  fontSize: 14,
  fontWeight: "600",
  color: colors.palette.neutral100,
}

const $arrow: ViewStyle = {
  width: 0,
  height: 0,
  // @ts-ignore
  position: "absolute",
  borderLeftWidth: 8,
  borderRightWidth: 8,
  borderLeftColor: "transparent",
  borderRightColor: "transparent",
}

const $arrowUp: ViewStyle = {
  top: -8,
  borderBottomWidth: 8,
  borderBottomColor: "rgba(255, 255, 255, 0.95)",
}

const $arrowDown: ViewStyle = {
  bottom: -8,
  borderTopWidth: 8,
  borderTopColor: "rgba(255, 255, 255, 0.95)",
}
